/**
 * Pointer "light source".
 *
 * Tracks the cursor and writes its position to CSS custom properties
 * (`--pointer-x` / `--pointer-y`, as percentages) on <html>. The glass-dark
 * theme renders a dim radial glow on `body::after` centred on those vars —
 * a layer that sits ABOVE the fixed backdrop gradient but BELOW all the
 * (translucent) UI, so the light appears to drift behind the frosted panels
 * as the mouse moves.
 *
 * Cheap + theme-aware: a single passive listener, rAF-batched, and it only
 * writes the vars while the dark glass theme is active (other themes don't
 * reference them, so there's nothing to update). Honours
 * prefers-reduced-motion by not attaching at all.
 */

const LIGHT_THEME_ID = 'glass-dark';

let started = false;
let raf = 0;
let xPct = 50;
let yPct = 50;

function flush(): void {
  raf = 0;
  const el = document.documentElement;
  el.style.setProperty('--pointer-x', `${String(xPct)}%`);
  el.style.setProperty('--pointer-y', `${String(yPct)}%`);
}

function onPointerMove(event: PointerEvent): void {
  // Only do work when the effect is actually visible.
  if (document.documentElement.dataset.theme !== LIGHT_THEME_ID) return;
  const w = window.innerWidth || 1;
  const h = window.innerHeight || 1;
  xPct = (event.clientX / w) * 100;
  yPct = (event.clientY / h) * 100;
  if (raf === 0) raf = window.requestAnimationFrame(flush);
}

/** Attach the pointer-light tracker (idempotent). Call once at startup. */
export function initPointerLight(): void {
  if (started || typeof window === 'undefined') return;
  started = true;
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  window.addEventListener('pointermove', onPointerMove, { passive: true });
}
